/**
 * Freezes/unfreezes the body background texture during sticky scenes.
 *
 * Uses a fixed-position overlay div that sits above the body background
 * but below all game content. This avoids the compositor-vs-main-thread
 * timing issue that causes shaking on mobile when compensating via
 * backgroundPositionY.
 *
 * On unfreeze the overlay fades out via opacity so the transition between
 * the frozen and scrolling background is smooth rather than an instant jump.
 */

let freezeCount = 0
let overlayEl: HTMLDivElement | null = null
let fadeTimer: ReturnType<typeof setTimeout> | null = null

function getOrCreateOverlay(): HTMLDivElement {
  if (overlayEl && overlayEl.isConnected) return overlayEl
  overlayEl = document.createElement('div')
  overlayEl.style.cssText =
    'position:fixed;inset:0;pointer-events:none;display:none;will-change:auto;'
  document.body.prepend(overlayEl)
  return overlayEl
}

export function freezeBackground(): void {
  freezeCount++
  if (freezeCount > 1) return

  if (fadeTimer) {
    clearTimeout(fadeTimer)
    fadeTimer = null
  }

  const computed = getComputedStyle(document.body)
  const overlay = getOrCreateOverlay()
  overlay.style.transition = ''
  overlay.style.opacity = '1'
  overlay.style.backgroundImage = computed.backgroundImage
  overlay.style.backgroundColor = computed.backgroundColor
  overlay.style.backgroundSize = computed.backgroundSize
  overlay.style.backgroundRepeat = computed.backgroundRepeat
  overlay.style.backgroundPositionX = computed.backgroundPositionX
  overlay.style.backgroundPositionY = `${-window.scrollY}px`
  overlay.style.display = 'block'
}

const FADE_MS = 150

export function unfreezeBackground(): void {
  if (freezeCount <= 0) return
  freezeCount--
  if (freezeCount > 0) return

  if (!overlayEl) return

  overlayEl.style.transition = `opacity ${FADE_MS}ms ease-out`
  overlayEl.style.opacity = '0'

  fadeTimer = setTimeout(() => {
    if (overlayEl && freezeCount === 0) {
      overlayEl.style.display = 'none'
      overlayEl.style.transition = ''
      overlayEl.style.opacity = ''
    }
    fadeTimer = null
  }, FADE_MS + 20)
}

export function destroyBackgroundFreeze(): void {
  freezeCount = 0
  if (fadeTimer) {
    clearTimeout(fadeTimer)
    fadeTimer = null
  }
  if (overlayEl && overlayEl.isConnected) {
    overlayEl.remove()
  }
  overlayEl = null
}
